/**
 * Phase 9 — Unified helper for building academy auth/dashboard URLs.
 *
 * On the platform host (dwrrah.com): returns `/${slug}/${path}` (slug-based).
 * On a custom academy domain: returns `/${path}` (no slug — domain == academy).
 *
 * Use this everywhere instead of hand-crafting `/${slug}/login`. Without it,
 * pressing "Login" on a custom domain navigates to `/dwrrah-academy/login`,
 * which has no matching route on the custom-domain Routes tree.
 */

import { isCustomAcademyHost, MAIN_DOMAIN_ORIGIN } from "@/lib/host-resolver";

function clean(p) {
  return String(p || "").replace(/^\/+|\/+$/g, "");
}

/**
 * Build a path on the active host for the given academy.
 *   - Custom domain: `/<path>`
 *   - Platform host: `/<slug>/<path>`
 */
export function buildAcademyPath(slug, path = "") {
  const cleanPath = clean(path);
  if (isCustomAcademyHost()) {
    return cleanPath ? `/${cleanPath}` : "/";
  }
  const cleanSlug = clean(slug);
  if (!cleanSlug) return cleanPath ? `/${cleanPath}` : "/";
  return cleanPath ? `/${cleanSlug}/${cleanPath}` : `/${cleanSlug}`;
}

/**
 * Convenience: build a login URL with optional `?redirect=<path>`.
 * `redirect` should be the destination path AFTER login on the same host.
 */
export function buildAcademyLoginUrl(slug, redirect = "") {
  const base = buildAcademyPath(slug, "login");
  const r = clean(redirect);
  if (!r) return base;
  const dest = isCustomAcademyHost() ? `/${r}` : `/${clean(slug)}/${r}`;
  return `${base}?redirect=${encodeURIComponent(dest)}`;
}

export function buildAcademySignupUrl(slug, redirect = "") {
  const base = buildAcademyPath(slug, "signup");
  const r = clean(redirect);
  if (!r) return base;
  const dest = isCustomAcademyHost() ? `/${r}` : `/${clean(slug)}/${r}`;
  return `${base}?redirect=${encodeURIComponent(dest)}`;
}

/**
 * Single source of truth for building **student-area** URLs.
 *
 * Always returns a path that begins with exactly one "/". This prevents the
 * dangerous protocol-relative URL bug where `/${slug}/${path}` with an empty
 * `slug` produces `//learning` — which the browser interprets as
 * `https://learning/` and fails with `DNS_PROBE_FINISHED_NXDOMAIN`.
 *
 * Behaviour:
 *   - On the platform host (dwrrah.com): returns `/${slug}/${path}`.
 *   - On a custom academy domain: returns `/${path}` (no slug — the host IS
 *     the academy).
 *   - When `slug` is empty: still returns `/${path}` safely.
 *
 * Use this everywhere in the student area instead of hand-crafting
 * `` `/${slug}/${path}` ``.
 */
export function buildStudentPath(slug, path = "") {
  return buildAcademyPath(slug, path);
}

/**
 * For Google OAuth on a custom domain we cannot use Google directly (the
 * domain is not registered as a redirect URI). Instead we send users to
 * dwrrah.com/post-login with `?return_to=<custom-domain-url>` so PostLogin
 * runs the encrypted handoff and lands them back on the custom domain
 * already authenticated.
 */
export function buildOAuthHandoffRedirect(returnDestination) {
  // returnDestination is a full URL on the custom domain.
  // `student_lock=1` tells PostLogin to skip platform-admin / instructor
  // routing and go straight to the custom-domain handoff. This guarantees
  // session isolation on the academy's domain — even if the same email is
  // a platform admin or an instructor of another academy, they enter this
  // domain strictly as a student of THIS academy.
  return `${MAIN_DOMAIN_ORIGIN}/post-login?return_to=${encodeURIComponent(returnDestination)}&student_lock=1`;
}

/**
 * Phase 17B — Single source of truth for the **absolute** login URL of an
 * academy, independent of the host the caller currently runs on.
 *
 * Resolution order:
 *   1. If `academy.custom_domain` is set AND `domain_status === "verified"`
 *      → `https://{custom_domain}/login[?redirect=…]`
 *   2. Else if a slug is available (from the academy or `fallbackSlug`)
 *      → `${MAIN_DOMAIN_ORIGIN}/{slug}/login[?redirect=…]`
 *   3. Else → `${MAIN_DOMAIN_ORIGIN}/login`
 *
 * Guarantees:
 *   - Always exactly one slash after the origin (no `//`).
 *   - Never returns `/undefined` segments.
 *   - Safe to call from any host (custom domain, platform, SSR).
 *
 * Use this for **logout redirects, handoff bailouts, and any cross-host
 * link to an academy login**. Do NOT use for in-page client-side navigation
 * on the SAME host — `buildAcademyLoginUrl(slug, redirect)` is the right
 * choice there because it stays host-relative.
 */
export function buildAbsoluteAcademyLoginUrl(academy, opts = {}) {
  const { fallbackSlug = "", returnPath = "" } = opts || {};

  const customDomain = String(academy?.custom_domain || "").trim().toLowerCase();
  const domainStatus = String(academy?.domain_status || "").trim().toLowerCase();
  const verified = !!customDomain && domainStatus === "verified";

  const slug = clean(academy?.slug || fallbackSlug || "");

  // Normalize returnPath to start with exactly one "/", or be empty.
  let redirectQs = "";
  if (returnPath) {
    const safePath = String(returnPath).startsWith("/")
      ? String(returnPath)
      : `/${String(returnPath).replace(/^\/+/, "")}`;
    redirectQs = `?redirect=${encodeURIComponent(safePath)}`;
  }

  if (verified) {
    return `https://${customDomain}/login${redirectQs}`;
  }

  // Phase 19B fallback — when the academy object passed in is missing the
  // domain fields (stale select / partial cache) but the user is *currently*
  // running on a non-platform host, we MUST stay on that same host for the
  // login screen. Otherwise tapping logout on `https://academy.example.com`
  // would redirect to `https://dwrrah.com/{slug}/login`, which breaks the
  // custom-domain experience even though the academy is correctly mapped.
  // This is safe: it never widens to an unrelated host — only the host the
  // user is already on, which already passed isCustomAcademyHost gating to
  // load the academy in the first place.
  if (typeof window !== "undefined" && window.location) {
    try {
      const currentHost = String(window.location.hostname || "").trim().toLowerCase();
      if (currentHost && isCustomAcademyHost(currentHost)) {
        return `https://${currentHost}/login${redirectQs}`;
      }
    } catch { /* fall through to platform fallback */ }
  }

  if (slug) {
    return `${MAIN_DOMAIN_ORIGIN}/${slug}/login${redirectQs}`;
  }
  return `${MAIN_DOMAIN_ORIGIN}/login${redirectQs}`;
}
